// File: StudentExitBackfill.js — ใส่ข้อมูลการออกย้อนหลัง (การ์ดในหน้าจำหน่ายนักเรียน)
//
// นักเรียนที่ถูกให้ออกก่อนจะมีหน้าจำหน่าย มีแต่ tb_student.st_status = '0' เปล่าๆ
// ไม่มีวันที่ ไม่มีสาเหตุ ไม่มีปลายทาง — ทะเบียนจึงตอบต้นสังกัดไม่ได้ว่าเด็กที่ออกก่อนจบ
// หายไปไหน การ์ดนี้คือทางที่จะเติมข้อมูลให้คนกลุ่มนั้นโดยไม่ต้องยิง SQL เอง
//
// ทำทีละรุ่น (กรองด้วยปีการศึกษาที่มีทะเบียนเป็นปีสุดท้าย) เพราะวันที่กับสาเหตุของคนรุ่น
// เดียวกันมักเป็นชุดเดียวกัน — ถ้าเลือกข้ามปีมาปนกัน วันที่ชุดเดียวจะผิดสำหรับบางคนแน่นอน
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Form, Modal, Row, Table } from "react-bootstrap";
import Swal from "sweetalert2";
import axiosInstance from "../services/axiosInterceptor";
import { toThaiDate } from "../admission/fields";
import ThaiDateInput from '../components/ThaiDateInput'
import {
  WITHDRAW_REASONS, TYPE_LABEL, todayISO, classLabel, islamicClassLabel,
  suggestedType, academicYearEndISO,
} from "./studentExitShared";

// ค่าว่าง = ไม่ทราบสาเหตุ ซึ่งเป็นคำตอบที่ตรงความจริงสำหรับข้อมูลเก่าหลายรุ่น
// การบังคับให้เลือกสาเหตุจะได้ "ลาออก" ที่ไม่มีใครยืนยัน ซึ่งแย่กว่าช่องว่าง
const UNKNOWN_REASON = "";

const EMPTY_FORM = {
  ex_type: "graduate",
  ex_date: "",
  reasonChoice: UNKNOWN_REASON,
  reasonOther: "",
  ex_destination: "",
  ex_note: "",
};

export default function StudentExitBackfill({ onDone }) {
  const [data, setData] = useState({ total: 0, rows: [] });
  const [year, setYear] = useState("");
  const [picked, setPicked] = useState(() => new Set());
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/api/studentExit/missing');
      setData(res.data?.data || { total: 0, rows: [] });
    } catch (err) {
      console.error('load students missing exit detail failed', err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ปีการศึกษาที่พบในรายการ เรียงใหม่ก่อน — ค่าว่างคือคนที่ไม่มีแถวใน tb_study เลย
  // (ข้อมูลเก่ามาก หรือทะเบียนถูกลบไปแล้ว) ต้องมีที่ให้เขาอยู่ ไม่งั้นจะไม่มีใครเห็น
  const years = useMemo(() => {
    const set = new Set(data.rows.map((r) => r.gen_year || ''));
    return [...set].sort((a, b) => String(b).localeCompare(String(a)));
  }, [data.rows]);

  useEffect(() => {
    if (years.length && !years.includes(year)) setYear(years[0]);
  }, [years, year]);

  const rows = useMemo(
    () => data.rows.filter((r) => (r.gen_year || '') === year),
    [data.rows, year]
  );

  // ล้างที่ติ๊กไว้เมื่อเปลี่ยนปี — ถ้าปล่อยค้าง คนที่ติ๊กจากปีก่อนจะถูกบันทึกด้วยวันที่ของ
  // ปีนี้ทั้งที่ไม่ได้อยู่บนจอแล้ว
  useEffect(() => { setPicked(new Set()); }, [year]);

  const toggle = (stId) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(stId)) next.delete(stId); else next.add(stId);
      return next;
    });
  };

  const allPicked = rows.length > 0 && rows.every((r) => picked.has(r.st_id));
  const toggleAll = () => setPicked(allPicked ? new Set() : new Set(rows.map((r) => r.st_id)));

  const openForm = () => {
    if (!picked.size) return;
    // เดาประเภทจากคนที่เลือกไว้: ถ้าอยู่ชั้นสุดท้ายของหลักสูตรกันทั้งหมดก็คือรุ่นที่จบ
    const chosen = rows.filter((r) => picked.has(r.st_id));
    const allFinal = chosen.every((r) => suggestedType(r) === 'graduate');
    setForm({
      ...EMPTY_FORM,
      ex_type: allFinal ? 'graduate' : 'withdraw',
      ex_date: academicYearEndISO(year) || todayISO(),
    });
    setShowForm(true);
  };

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const isWithdraw = form.ex_type === 'withdraw';
  const reason = isWithdraw
    ? (form.reasonChoice === 'อื่นๆ' ? form.reasonOther.trim() : form.reasonChoice)
    : '';

  const handleSave = async () => {
    if (!form.ex_date) {
      Swal.fire({ icon: 'warning', title: 'ยังไม่ได้ระบุวันที่' });
      return;
    }
    const ids = rows.filter((r) => picked.has(r.st_id)).map((r) => r.st_id);
    const confirm = await Swal.fire({
      icon: 'question',
      title: 'ใส่ข้อมูลย้อนหลัง?',
      html: `<div class="text-start">
        <div>นักเรียน <b>${ids.length}</b> คน (ปีการศึกษา ${year || 'ไม่มีทะเบียน'})</div>
        <div>ประเภท ${TYPE_LABEL[form.ex_type]}</div>
        <div>วันที่ ${toThaiDate(form.ex_date) || form.ex_date}</div>
        <div>สาเหตุ ${reason || 'ไม่ระบุ'}</div>
      </div>
      <div class="mt-2 fs-sm text-secondary">สถานะนักเรียนและทะเบียนหอพักไม่เปลี่ยน —
      บันทึกนี้เป็นการเติมรายละเอียดของการออกที่เกิดขึ้นไปแล้ว</div>`,
      showCancelButton: true,
      confirmButtonText: 'บันทึก',
      cancelButtonText: 'ยกเลิก',
    });
    if (!confirm.isConfirmed) return;

    setSaving(true);
    try {
      const res = await axiosInstance.post('/api/studentExit/backfill', {
        student_ids: ids,
        ex_type: form.ex_type,
        ex_date: form.ex_date,
        ex_reason: reason || null,
        ex_destination: form.ex_destination.trim() || null,
        ex_note: form.ex_note.trim() || null,
      });
      setShowForm(false);
      setPicked(new Set());
      Swal.fire({ icon: 'success', title: res.data?.message || 'บันทึกแล้ว', timer: 3000, showConfirmButton: false });
      await load();
      if (onDone) await onDone();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'บันทึกไม่สำเร็จ', text: err.response?.data?.message || 'เกิดข้อผิดพลาด' });
    } finally {
      setSaving(false);
    }
  };

  if (!data.total) return null;

  return (
    <React.Fragment>
      <Card className="card-one mb-4 border-danger">
        <Card.Header>
          <Card.Title as="h6" className="mb-0">
            จำหน่ายแล้วแต่ยังไม่มีรายละเอียด ({data.total} คน)
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <div className="fs-sm text-secondary mb-3">
            คนกลุ่มนี้ถูกให้ออกจากทะเบียนตั้งแต่ก่อนมีหน้านี้ ระบบเดิมบันทึกไว้แค่ว่า "ออกแล้ว"
            ไม่มีวันที่ สาเหตุ หรือปลายทาง — เลือกทีละรุ่นแล้วเติมข้อมูลให้ตรงกับที่ทะเบียนมีอยู่
            {data.rows.length < data.total && (
              <span className="text-danger"> (แสดง {data.rows.length} คนแรกจาก {data.total} คน)</span>
            )}
          </div>

          <Row className="g-2 align-items-end mb-3">
            <Col md="4">
              <Form.Label className="fs-sm mb-1">ปีการศึกษาที่มีทะเบียนเป็นปีสุดท้าย</Form.Label>
              <Form.Select value={year} onChange={(e) => setYear(e.target.value)}>
                {years.map((y) => (
                  <option key={y || 'none'} value={y}>
                    {y || 'ไม่มีทะเบียนชั้นเรียน'} ({data.rows.filter((r) => (r.gen_year || '') === y).length} คน)
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md="8" className="text-md-end">
              <span className="fs-sm text-secondary me-3">เลือกแล้ว {picked.size} คน</span>
              <Button variant="danger" disabled={!picked.size} onClick={openForm}>
                <i className="ri-history-line"></i> ใส่ข้อมูลย้อนหลังให้ที่เลือก
              </Button>
            </Col>
          </Row>

          <div className="table-responsive" style={{ maxHeight: 420, overflowY: 'auto' }}>
            <Table className="table-bordered mb-0" hover>
              <thead className="table-secondary">
                <tr>
                  <th style={{ width: 44 }} className="text-center">
                    <Form.Check
                      type="checkbox"
                      checked={allPicked}
                      onChange={toggleAll}
                      aria-label="เลือกทั้งหมดในปีนี้"
                    />
                  </th>
                  <th style={{ width: 120 }}>เลขประจำตัว</th>
                  <th>ชื่อ - สกุล</th>
                  <th style={{ width: 130 }} className="text-center">ชั้นสามัญ</th>
                  <th style={{ width: 120 }} className="text-center">ชั้นศาสนา</th>
                  <th style={{ width: 180 }}>หอพัก</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan="6" className="text-center text-secondary py-4">ไม่มีรายการในปีนี้</td></tr>
                )}
                {rows.map((row) => (
                  <tr key={row.st_id} onClick={() => toggle(row.st_id)} style={{ cursor: 'pointer' }}>
                    <td className="text-center">
                      <Form.Check
                        type="checkbox"
                        checked={picked.has(row.st_id)}
                        onChange={() => toggle(row.st_id)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={row.st_name}
                      />
                    </td>
                    <td>{row.st_idnumber}</td>
                    <td>{row.st_name}</td>
                    <td className="text-center">{classLabel(row)}</td>
                    <td className="text-center">{islamicClassLabel(row)}</td>
                    <td className="fs-sm">
                      {row.dorm_name
                        ? <Badge bg="warning">{row.dorm_name} — ยังค้างเตียง</Badge>
                        : <span className="text-secondary">ไม่มีทะเบียนหอพัก</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      <Modal show={showForm} onHide={() => setShowForm(false)} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title as="h6" className="mb-0">
            ใส่ข้อมูลการออกย้อนหลัง — {picked.size} คน (ปีการศึกษา {year || 'ไม่มีทะเบียน'})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md="12">
              <Form.Label className="d-block">ประเภท</Form.Label>
              <Form.Check
                inline type="radio" id="backfill-type-graduate" label="สำเร็จการศึกษา (จบ ม.3 / ม.6)"
                checked={form.ex_type === 'graduate'}
                onChange={() => setField('ex_type', 'graduate')}
              />
              <Form.Check
                inline type="radio" id="backfill-type-withdraw" label="จำหน่าย (ออกก่อนจบหลักสูตร)"
                checked={isWithdraw}
                onChange={() => setField('ex_type', 'withdraw')}
              />
              <div className="fs-sm text-secondary mt-1">
                ค่าที่กรอกที่นี่ใช้กับทุกคนที่เลือกไว้ — ถ้าในรุ่นเดียวกันมีทั้งคนจบและคนออกกลางคัน
                ให้แยกบันทึกสองรอบ
              </div>
            </Col>

            <Col md="6">
              <Form.Label>วันที่{isWithdraw ? 'จำหน่าย' : 'สำเร็จการศึกษา'}</Form.Label>
              <ThaiDateInput
                value={form.ex_date}
                onChange={v => setField('ex_date', v)}
              />
              <div className="fs-sm text-secondary mt-1">
                {form.ex_date ? `ตรงกับ ${toThaiDate(form.ex_date)} (พ.ศ.)` : 'ยังไม่ได้เลือกวันที่'}
                {' '}— ค่าเริ่มต้นคือวันสิ้นปีการศึกษา {year || ''} ซึ่งเป็นค่าประมาณ
                {' '}แก้เป็นวันจริงได้ถ้ามีในเอกสารของทะเบียน
              </div>
            </Col>

            <Col md="6">
              <Form.Label>สาเหตุ{isWithdraw ? '' : ' (ไม่ต้องระบุ)'}</Form.Label>
              <Form.Select
                value={form.reasonChoice}
                disabled={!isWithdraw}
                onChange={(e) => setField('reasonChoice', e.target.value)}
              >
                <option value={UNKNOWN_REASON}>ไม่ทราบ — ข้อมูลเดิมไม่ได้บันทึกไว้</option>
                {WITHDRAW_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </Form.Select>
              {isWithdraw && form.reasonChoice === 'อื่นๆ' && (
                <Form.Control
                  className="mt-2"
                  maxLength={100}
                  placeholder="ระบุสาเหตุ"
                  value={form.reasonOther}
                  onChange={(e) => setField('reasonOther', e.target.value)}
                />
              )}
              {isWithdraw && !reason && (
                <div className="fs-sm text-secondary mt-1">
                  เว้นว่างได้ — ดีกว่าเดาสาเหตุที่ไม่มีใครยืนยัน
                </div>
              )}
            </Col>

            <Col md="12">
              <Form.Label>ไปเรียนต่อที่ (ใส่เมื่อทั้งรุ่นไปที่เดียวกัน)</Form.Label>
              <Form.Control
                maxLength={255}
                placeholder="ปล่อยว่างไว้ถ้าแต่ละคนไปคนละที่ แล้วค่อยแก้รายคนจากช่องค้นหา"
                value={form.ex_destination}
                onChange={(e) => setField('ex_destination', e.target.value)}
              />
            </Col>

            <Col md="12">
              <Form.Label>หมายเหตุ</Form.Label>
              <Form.Control
                maxLength={255}
                value={form.ex_note}
                onChange={(e) => setField('ex_note', e.target.value)}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowForm(false)} disabled={saving}>ปิด</Button>
          <Button variant="danger" onClick={handleSave} disabled={saving || !picked.size}>
            {saving ? 'กำลังบันทึก...' : `บันทึกย้อนหลัง ${picked.size} คน`}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
}
